/**
 * Central location registry for DD Consulting.
 *
 * Single source of truth consumed by:
 *   • App.js Organisation JSON-LD
 *   • Home GlobalPresenceSection
 *   • ContactPage
 *   • Footer (satellite strip)
 *   • Reusable <GlobalNodesStrip /> mini-strips on page headers
 *
 * Positioning model — three tiers:
 *   Tier 1 (Local):     Sydney HQ, established 2020
 *   Tier 2 (National):  Servicing clients across all Australian states
 *   Tier 3 (Global):    7 international satellite offices
 *
 * Editing rules:
 *   • Sydney must always be the first entry — HQ ordering is enforced in code
 *     that reads this array by using getHQ() / getSatellites() helpers.
 *   • `code` values feed into JSON-LD addressCountry — must be valid ISO-3166 alpha-2.
 *   • `timezone` values must be valid IANA identifiers (used by GlobalSyncClock).
 */

// ─── Australian national coverage ────────────────────────────────────────
export const AU_STATES = [
  { code: 'NSW', name: 'New South Wales' },
  { code: 'VIC', name: 'Victoria' },
  { code: 'QLD', name: 'Queensland' },
  { code: 'WA',  name: 'Western Australia' },
  { code: 'SA',  name: 'South Australia' },
  { code: 'ACT', name: 'Australian Capital Territory' },
  { code: 'TAS', name: 'Tasmania' },
  { code: 'NT',  name: 'Northern Territory' },
];

// ─── The eight nodes: 1 HQ + 7 satellite offices ─────────────────────────
export const NODES = [
  {
    id: 'sydney',
    city: 'Sydney',
    country: 'Australia',
    countryCode: 'AU',
    flag: '🇦🇺',
    timezone: 'Australia/Sydney',
    tzLabel: 'SYD',
    coords: '33.7318° S · 150.9636° E',
    latitude: -33.7318,
    longitude: 150.9636,
    isHQ: true,
    role: 'Headquarters',
    established: 2020,
    tagline: 'Strategy · Contracting · Client success',
    // Registered HQ street address (Norwest, Sydney Hills District). Single
    // source of truth for NAP (Name · Address · Phone) consistency across the
    // footer, Contact page and every PostalAddress in JSON-LD.
    address: {
      streetAddress: 'Level 5, Nexus Norwest, 4 Columbia Court',
      addressLocality: 'Norwest',
      addressRegion: 'NSW',
      postalCode: '2153',
      addressCountry: 'AU',
    },
  },
  {
    id: 'colombo',
    city: 'Colombo',
    country: 'Sri Lanka',
    countryCode: 'LK',
    flag: '🇱🇰',
    timezone: 'Asia/Colombo',
    tzLabel: 'CMB',
    coords: '6.9271° N · 79.8612° E',
    latitude: 6.9271,
    longitude: 79.8612,
    isHQ: false,
    role: 'Satellite Office',
    tagline: 'Engineering delivery',
  },
  {
    id: 'mumbai',
    city: 'Mumbai',
    country: 'India',
    countryCode: 'IN',
    flag: '🇮🇳',
    timezone: 'Asia/Kolkata',
    tzLabel: 'BOM',
    coords: '19.0760° N · 72.8777° E',
    latitude: 19.076,
    longitude: 72.8777,
    isHQ: false,
    role: 'Satellite Office',
    tagline: 'Engineering delivery',
  },
  {
    id: 'gujarat',
    city: 'Gujarat',
    country: 'India',
    countryCode: 'IN',
    flag: '🇮🇳',
    timezone: 'Asia/Kolkata',
    tzLabel: 'GUJ',
    coords: '22.2587° N · 71.1924° E',
    latitude: 22.2587,
    longitude: 71.1924,
    isHQ: false,
    role: 'Satellite Office',
    tagline: 'Engineering delivery',
  },
  {
    id: 'singapore',
    city: 'Singapore',
    country: 'Singapore',
    countryCode: 'SG',
    flag: '🇸🇬',
    timezone: 'Asia/Singapore',
    tzLabel: 'SGP',
    coords: '1.3521° N · 103.8198° E',
    latitude: 1.3521,
    longitude: 103.8198,
    isHQ: false,
    role: 'Satellite Office',
    tagline: 'APAC financial-services diligence',
  },
  {
    id: 'hcmc',
    city: 'Ho Chi Minh City',
    country: 'Vietnam',
    countryCode: 'VN',
    flag: '🇻🇳',
    timezone: 'Asia/Ho_Chi_Minh',
    tzLabel: 'SGN',
    coords: '10.8231° N · 106.6297° E',
    latitude: 10.8231,
    longitude: 106.6297,
    isHQ: false,
    role: 'Satellite Office',
    tagline: 'AI / ML engineering',
  },
  {
    id: 'fiji',
    city: 'Fiji',
    country: 'Fiji',
    countryCode: 'FJ',
    flag: '🇫🇯',
    timezone: 'Pacific/Fiji',
    tzLabel: 'FJI',
    coords: '17.7134° S · 178.0650° E',
    latitude: -17.7134,
    longitude: 178.065,
    isHQ: false,
    role: 'Satellite Office',
    tagline: 'Operations & customer success',
  },
  {
    id: 'newark',
    city: 'Newark',
    country: 'United States',
    countryCode: 'US',
    flag: '🇺🇸',
    timezone: 'America/New_York',
    tzLabel: 'NJ',
    coords: '40.7357° N · 74.1724° W',
    latitude: 40.7357,
    longitude: -74.1724,
    isHQ: false,
    role: 'Satellite Office',
    tagline: '24/7 support · US client coverage',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────
export const getHQ = () => NODES.find((n) => n.isHQ);
export const getSatellites = () => NODES.filter((n) => !n.isHQ);

/** Countries served — used by JSON-LD areaServed and canonical copy. */
export const COUNTRIES_SERVED = [
  { name: 'Australia', code: 'AU' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'India', code: 'IN' },
  { name: 'Singapore', code: 'SG' },
  { name: 'Vietnam', code: 'VN' },
  { name: 'Fiji', code: 'FJ' },
  { name: 'United States', code: 'US' },
];

/** Canonical positioning taglines — reused across surfaces. */
export const POSITIONING = {
  short: 'Australian HQ · Australia-wide · 7 satellite offices',
  medium: 'Sydney-headquartered. Australia-wide delivery. Global engineering bench.',
  long: 'Sydney-headquartered strategic engineering consultancy servicing clients across all Australian states and territories, with satellite offices in Colombo, Mumbai, Gujarat, Singapore, Ho Chi Minh City, Fiji and Newark.',
};

// ─── NAP — Name · Address · Phone (must be byte-identical everywhere) ───────
export const NAP = {
  name: 'DD Consulting',
  legalName: 'DD Consulting Pty Ltd',
  abn: '65 646 596 867',
  phoneDisplay: '+61 484 105 707',
  phoneE164: '+61484105707',
  email: 'info@ddconsult.tech',
  /** Google Business Profile share link (verified listing: "DD Consulting Pty Ltd"). */
  googleBusinessProfile: 'https://share.google/27IVxfJkJ7vR6UL4z',
  address: getHQ().address,
  /** One-line human-readable address. */
  addressLine: 'Level 5, Nexus Norwest, 4 Columbia Court, Norwest NSW 2153, Australia',
  /** schema.org PostalAddress for the HQ. */
  postalAddress: () => ({ '@type': 'PostalAddress', ...getHQ().address }),
};
